import React, { FC, useMemo } from 'react';
import { AvatarGroup, Box, Paper, Stack, Typography } from '@mui/material';

import HiddenItemCounter from '@/components/HiddenItemCounter/HiddenItemCounter';
import { UserAvatar } from '@/components/UserAvatar';
import { useDirection } from '@/hooks/useDirection';
import typography from '@/theme/typography';
import { BasicUser, getUserFullName } from '@/utils/user';

const VISIBLE_ITEMS_COUNT = 6;

interface UserDisplayProps {
  value?: BasicUser[];
  isArchived?: boolean;
  totalCount?: number;
}

const AVATAR_SIZE = 24;
const AVATAR_POPPER_SIZE = 20;

const UserDisplay: FC<UserDisplayProps> = ({ value, isArchived, totalCount }) => {
  const { isRtl } = useDirection();

  const hiddenItems = useMemo(() => value?.slice(VISIBLE_ITEMS_COUNT) ?? [], [value]);

  const counterValue = useMemo(() => {
    if (totalCount) {
      return totalCount - (value?.length ?? 0);
    }

    return hiddenItems?.length;
  }, [hiddenItems?.length, totalCount, value?.length]);

  const showDropdown = !totalCount;

  if (!value) return null;

  return (
    <Box display="flex" alignItems="center" height={AVATAR_SIZE} gap={isRtl ? 1 : 0}>
      <AvatarGroup
        max={VISIBLE_ITEMS_COUNT}
        sx={{
          alignItems: 'center',
          m: 0,
          '& .MuiAvatar-root': {
            width: AVATAR_SIZE,
            height: AVATAR_SIZE,
            ...typography.body2
          },
          '& .MuiAvatar-root:last-of-type': {
            ml: -1
          },
          '& .MuiAvatar-root:first-of-type': {
            display: value.length > VISIBLE_ITEMS_COUNT ? 'none' : 'inherit'
          }
        }}
      >
        {value.map(user => (
          <UserAvatar key={user.id} user={user} size={AVATAR_SIZE} />
        ))}
      </AvatarGroup>

      <Stack alignItems="center">
        <HiddenItemCounter hiddenItemsCount={counterValue} isDisabled={isArchived}>
          {showDropdown && (
            <Paper
              sx={{
                position: 'relative',
                minWidth: '87px',
                p: 1.5,
                '& .MuiTypography-root': { color: 'text.primary', ...typography.body3 }
              }}
            >
              <Stack maxHeight="200px" overflow="auto" gap={1}>
                {hiddenItems.map(user => (
                  <Stack
                    key={user.id}
                    direction="row"
                    alignItems="center"
                    gap={1}
                    p={0.5}
                    sx={{
                      borderRadius: ({ spacing }) => spacing(0.5),
                      '&:hover': {
                        bgcolor: 'primary.200'
                      }
                    }}
                  >
                    <UserAvatar user={user} size={AVATAR_POPPER_SIZE} />
                    <Typography>{getUserFullName(user)}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Paper>
          )}
        </HiddenItemCounter>
      </Stack>
    </Box>
  );
};

export default UserDisplay;
